import React, { useEffect, useState } from 'react';
import { Icon, IconButton, Spinner, SpinnerSize, Text } from '@fluentui/react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

// component Functions
import * as ComponentFunctions from './PractitionerConcept.Item.Functions';

// components
import PractitionerProtocolConceptFormComponent from '../../PractitionerProtocolConceptsForm/PractitionerProtocolConceptForm.Component';
import ConfirmDialog from '../../../../../commons/component/ConfirmDialog/ConfirmDialog';

// styles
import './PractitionerConcept.Item.Styles.css';



const PractitionerConceptItem = (props: ComponentFunctions.Props) => {

    const { t } = useTranslation();

    const [isOpenItemConceptForm, setIsOpenItemConceptForm] = useState(false);
    const openSettingFormPanel = () => setIsOpenItemConceptForm(true);
    const dismissSettingFormPanel = () => setIsOpenItemConceptForm(false);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // useEffects
    useEffect(() => {
        if (props.ContractId) {
            props.GetContractById(props.CommissionAgentConcept.contractId);
        }
    }
    , [props.Contract == undefined]);

    useEffect(() => {
        if (props.CommissionAgentId) {
            props.GetContractCommissionAgentById(props.CommissionAgentId, props.Contract.id);
        }
    }
    , [props.Contract !== undefined && props.Agent === undefined]);

    useEffect(() => {
        if (props.SavingSuccessfully && isOpenItemConceptForm) {
            dismissSettingFormPanel();
        }
    }, [props.SavingSuccessfully]);

    // events
    const onClick = () => {
        openSettingFormPanel();
        props.onClick && props.onClick(props.CommissionConcept.id);
    };

    const onDelete = (e: any) => {
        e.stopPropagation();
        setConfirmDeleteOpen(true);
    }

    const onConfirmDelete = () => {
        setConfirmDeleteOpen(false);
        setIsDeleting(true);
        props.Agent && props.DeleteContractCommissionAgentConcept(props.Agent.id, props.CommissionAgentConcept.id, props.CommissionAgentConcept.contractId);
    }

    const onCancelDelete = () => {
        setConfirmDeleteOpen(false);
    }

    const onCancelForm = () => dismissSettingFormPanel();

    const itemTitle = props.CommissionConcept?.payByForm
        ? `${t('practitionerConcepts.badges.byForm', 'Pago por formulario')}: ${props.CommissionConcept.formTitle || `#${props.CommissionConcept.formId ?? ''}`}`
        : props.CommissionConcept?.payByVisit
            ? t('practitionerConcepts.badges.byVisit', 'Pago por visita')
            : `${t('practitionerConcepts.conceptLabel', 'Pago por concepto')}: ${props.Concept?.concept.conceptName || ''}`;

    // Formatear información del concepto
    const getConceptInfo = () => {
        if (!props.CommissionConcept) return '';
        const parts = [];
        
        // Determinar el tipo de valorización
        switch (props.CommissionConcept.valuationType) {
            case 1: // Valor Fijo
                parts.push('Valor Fijo');
                if (props.CommissionConcept.value > 0) {
                    parts.push(`${props.CommissionConcept.currencyCode} ${props.CommissionConcept.value}`);
                }
                break;
            case 3: // Porcentaje
                parts.push('Porcentaje');
                if (props.CommissionConcept.value > 0) {
                    parts.push(`${props.CommissionConcept.value}%`);
                }
                break;
            case 4: // Lista de Precio
                parts.push('Por Lista de Precio');
                break;
            default:
                parts.push(`Tipo ${props.CommissionConcept.valuationType}`);
                break;
        }
        
        if (props.CommissionConcept.remoteValue > 0) {
            parts.push(`Remoto: ${props.CommissionConcept.currencyCode} ${props.CommissionConcept.remoteValue}`);
        }
        
        // Indicar sobre qué se aplica
        if (props.CommissionConcept.takeOnAlreadyPaid) {
            parts.push('Sobre lo cobrado');
        } else if (props.CommissionConcept.takeOnAlreadyRealized) {
            parts.push('Sobre lo realizado');
        }
        
        return parts.join(' • ');
    };

    const Forms = (
        <>
            <PractitionerProtocolConceptFormComponent
                isOpen={isOpenItemConceptForm}
                openPanel={() => undefined}
                dismissPanel={onCancelForm}
                CommissionAgentId={props.CommissionAgentConcept.commissionAgentId}
                CommissionAgentConceptId={props.CommissionAgentConcept.id}
                ContractId={props.CommissionAgentConcept.contractId}
            />
        </>
    )

    return (
        <div className='CommissionAgentConceptContainer'>
            <button
                type='button'
                className='CommissionAgentConceptMainAction'
                onClick={onClick}
                disabled={props.disabled}
            >
                <div className='CommissionAgentConceptHeader'>
                    <div className='CommissionAgentConceptTitle'>
                        <Icon iconName='Calculate' className='CommissionAgentConceptTitleIcon' />
                        <Text variant='mediumPlus'>{itemTitle}</Text>
                    </div>
                </div>

                <div className='CommissionAgentConceptDetails'>
                    {getConceptInfo() || t('practitionerConcepts.noInfo', 'Sin informacion adicional')}
                </div>
            </button>

            <div className='CommissionAgentConceptActions'>
                <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    disabled={props.disabled}
                    onClick={onClick}
                    title={t('common.edit', 'Editar')}
                    ariaLabel={t('common.edit', 'Editar')}
                />
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    disabled={props.disabled || isDeleting}
                    onClick={onDelete}
                    title={t('common.delete', 'Eliminar')}
                    ariaLabel={t('common.delete', 'Eliminar')}
                    {...(isDeleting && { onRenderIcon: () => <Spinner size={SpinnerSize.small} /> })}
                />
            </div>

            {Forms}
            <ConfirmDialog
                open={confirmDeleteOpen}
                title={t('practitionerConcepts.confirmDelete.title', 'Confirmar eliminación')}
                message={t('practitionerConcepts.confirmDelete.message', '¿Está seguro que desea eliminar este concepto?')}
                highlight={props.CommissionConcept?.payByForm ? props.CommissionConcept.formTitle : props.CommissionConcept?.payByVisit ? 'Pago por visita' : props.Concept?.concept.conceptName}
                confirmText={t('common.delete', 'Eliminar')}
                cancelText={t('common.cancel', 'Cancelar')}
                onCancel={onCancelDelete}
                onConfirm={onConfirmDelete}
            />
        </div>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PractitionerConceptItem as any);